<template>
  <div class="student-dashboard-page">
    <!-- Header with just Viva logo -->
    <div class="viva-page-header-with-action">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="true"
        current-portal="assignment"
      />
    </div>

    <!-- Student Info Section -->
    <div class="instructor-info">
      <h2 v-if="studentName">Student: {{ studentName }}</h2>
      <h1 class="viva-page-title">MY COURSES</h1>
    </div>

    <!-- Course Cards Grid -->
    <div class="viva-card-grid">
      <!-- Loading state -->
      <div v-if="loading" class="empty-state">
        <i class="glyphicon glyphicon-refresh spinning"></i>
        <h3>Loading courses...</h3>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="empty-state">
        <i class="glyphicon glyphicon-exclamation-sign"></i>
        <h3>Error</h3>
        <p>{{ error }}</p>
        <button class="viva-btn viva-btn-green" @click="loadCourses">Try Again</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="courses.length === 0" class="empty-state">
        <i class="glyphicon glyphicon-book"></i>
        <h3>No Courses Found</h3>
        <p>You don't have any active courses yet.</p>
      </div>

      <!-- Course cards -->
      <template v-else>
        <div
          v-for="course in courses"
          :key="course.id"
          class="viva-card viva-card-green"
          @click="viewCourse(course.id)"
        >
          <div class="card-title">{{ course.title }}</div>
          <div class="card-subtitle" v-if="course.term || course.year">
            {{ course.term }}{{ course.term && course.year ? ', ' : '' }}{{ course.year }}
          </div>
          <div class="card-stats">
            <span class="stats-number">{{ course.assignment_count }}</span>
            <span class="stats-label">Assignment{{ course.assignment_count !== 1 ? 's' : '' }}</span>
          </div>
          <div class="card-stats" v-if="course.completed_count > 0">
            <span class="stats-number">{{ course.completed_count }}</span>
            <span class="stats-label">Completed</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import { handleApiError } from '../utils/errorHandler.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';

export default {
  name: 'StudentDashboard',
  components: {
    ProfileDropdown
  },

  data() {
    return {
      courses: [],
      studentName: '',
      loading: true,
      error: null
    };
  },

  async mounted() {
    await this.loadCourses();
  },

  methods: {
    async loadCourses() {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiClient.get('/api/viva/student/dashboard/');

        if (response.data) {
          this.courses = response.data.courses;
          this.studentName = response.data.student_name;
        }
      } catch (error) {
        console.error('Failed to load courses:', error);
        this.error = handleApiError(error, this.$router, 'Failed to load courses. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    viewCourse(courseId) {
      // Set loading immediately when card is clicked
      this.$root.$emit('show-loading');
      this.$router.push({
        name: 'student-course-assignments',
        params: { id: courseId }
      });
    }
  }
};
</script>

<style scoped>
.student-dashboard-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  padding-bottom: 50px;
}

.instructor-info {
  margin-bottom: 0;
  padding-bottom: 10px;
}

.instructor-info h2 {
  margin-bottom: 40px;
}

.viva-card-grid {
  padding-top: 10px;
  margin-left: 30px;
  margin-right: 30px;
  padding-left: 30px;
  padding-right: 30px;
}

.card-stats {
  margin-top: 15px;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.stats-number {
  font-size: 28px;
  font-weight: 700;
  color: var(--viva-dark);
}

.stats-label {
  font-size: 14px;
  color: var(--viva-grey);
  text-transform: uppercase;
}
</style>
